import React from 'react';

import type { IntakeStatusBaseSteps } from '@shared/patient-intake-status';

import { MHRAccessViewer } from '../../../components';
import { HealthcareSummaryViewer } from './HealthcareSummaryViewer.tsx';
import { IdVerificationViewer } from './DigitalIdVerificationViewer.tsx';
import { MedicareVerificationViewer } from './MedicareVerificationViewer.tsx';
import { ReferralLetterOfSupportViewer } from './ReferralLetterOfSupportViewer.tsx';
import { GenerateConsentToReleaseHealthSummaryViewer } from './GenerateConsentToReleaseHealthSummaryViewer.tsx';
import { AccountVerificationViewer } from './AccountVerificationViewer.tsx';

const BASE_STEP_VIEWERS = {
  ID_VERIFICATION: IdVerificationViewer,
  MEDICARE_VERIFICATION: MedicareVerificationViewer,
  UPLOAD_HEALTHCARE_SUMMARY: HealthcareSummaryViewer,
  GENERATE_CONSENT_TO_RELEASE_HEALTH_SUMMARY: GenerateConsentToReleaseHealthSummaryViewer,
  UPLOAD_REFERRAL_LETTER_OF_SUPPORT: ReferralLetterOfSupportViewer,
  MHR_ACCESS: MHRAccessViewer,
  ACCOUNT_VERIFICATION: AccountVerificationViewer,
} satisfies {
  [K in keyof IntakeStatusBaseSteps]: React.ComponentType<{ step: IntakeStatusBaseSteps[K] }>;
};

const getBaseStepViewer = <TBaseField extends keyof IntakeStatusBaseSteps>(baseField: TBaseField) => {
  return BASE_STEP_VIEWERS[baseField] as React.ComponentType<{ step: IntakeStatusBaseSteps[TBaseField] }>;
};

type CurrentStatusViewerProps<TBaseField extends keyof IntakeStatusBaseSteps> = {
  baseField: TBaseField;
  step: IntakeStatusBaseSteps[NoInfer<TBaseField>];
};

/**
 * This is a component that displays the current status metadata of a base step. E.g., say the user
 * has uploaded a health summary, the current status viewer will display the health summary details.
 */
export const CurrentStatusViewer = <TBaseField extends keyof IntakeStatusBaseSteps>({
  baseField,
  step,
}: CurrentStatusViewerProps<TBaseField>) => {
  if (!step) {
    return null;
  }

  const Viewer = getBaseStepViewer(baseField);

  if (!Viewer) {
    throw new Error(`Unknown base step type: ${baseField}`);
  }

  return <Viewer step={step} />;
};
